import React, { useEffect, useRef, useState } from "react";
import { Mail, Lock, ShieldCheck, X } from "lucide-react";
import trackCheckoutIntent from "../lib/trackCheckoutIntent";
import { useUserAuth } from "../contexts/UserAuthContext";

/**
 * CheckoutGate
 * --------------------------------------------------------------------------
 * Globally intercepts every click on a Stripe Payment Link (`buy.stripe.com`)
 * across the public site. If the visitor has no email associated with the
 * click (not signed in, no cached email) we open a one-field modal asking for
 * one. The captured email is then:
 *   1. Stored in localStorage for 30 days (skips the modal on repeat clicks)
 *   2. Appended to the Stripe URL via `prefilled_email=`
 *   3. Attached to the checkout-intent record so abandoned-cart recovery
 *      emails can actually fire
 *
 * Mounted once at the App root. No per-button refactor required.
 */
const EMAIL_KEY = "tdf_checkout_email";
const EMAIL_TTL_DAYS = 30;
const SKIP_KEY = "tdf_checkout_skip_email_until";
const SKIP_TTL_HOURS = 4; // After a skip we give the visitor a quiet window

function readJSON(key) {
  try {
    const raw = localStorage.getItem(key);
    return raw ? JSON.parse(raw) : null;
  } catch (e) {
    return null;
  }
}

function writeJSON(key, value) {
  try {
    localStorage.setItem(key, JSON.stringify(value));
  } catch (e) {
    // Storage unavailable / blocked — silent by design.
  }
}

function readCachedEmail() {
  const rec = readJSON(EMAIL_KEY);
  if (!rec || !rec.email || !rec.expires_at) return null;
  if (Date.now() > rec.expires_at) return null;
  return rec.email;
}

function persistEmail(email) {
  writeJSON(EMAIL_KEY, {
    email,
    expires_at: Date.now() + EMAIL_TTL_DAYS * 24 * 60 * 60 * 1000,
  });
}

function isSkipActive() {
  const rec = readJSON(SKIP_KEY);
  if (!rec || !rec.until) return false;
  return Date.now() < rec.until;
}

function markSkip() {
  writeJSON(SKIP_KEY, {
    until: Date.now() + SKIP_TTL_HOURS * 60 * 60 * 1000,
  });
}

function getSignedInEmail() {
  // Best-effort: try the cached site_user JSON (some flows persist it).
  try {
    const raw = localStorage.getItem("tdf_user");
    if (raw) {
      const parsed = JSON.parse(raw);
      if (parsed?.email) return parsed.email;
    }
  } catch (e) {
    // Ignore.
  }
  // Fallback: decode JWT payload from `tdf_user_token` (best-effort).
  try {
    const tok = localStorage.getItem("tdf_user_token");
    if (!tok) return null;
    const parts = tok.split(".");
    if (parts.length < 2) return null;
    const json = atob(parts[1].replace(/-/g, "+").replace(/_/g, "/"));
    const payload = JSON.parse(json);
    return payload?.email || payload?.sub || null;
  } catch (e) {
    return null;
  }
}

function appendPrefilledEmail(url, email) {
  if (!url || !email) return url;
  try {
    const u = new URL(url);
    u.searchParams.set("prefilled_email", email);
    return u.toString();
  } catch (e) {
    return url;
  }
}

function deriveProductMeta(anchor) {
  // Pull data-* attributes if the dev supplied them; otherwise fall back to
  // the link text + the URL path slug. Good enough for tracking + attribution.
  const ds = anchor.dataset || {};
  const text =
    (anchor.textContent || "").trim().replace(/\s+/g, " ").slice(0, 120) ||
    "Checkout";
  let urlSlug = "";
  try {
    urlSlug = new URL(anchor.href).pathname.replace(/^\/+|\/+$/g, "");
  } catch (e) {
    urlSlug = "";
  }
  return {
    productKey: ds.productKey || urlSlug || "stripe",
    productTitle: ds.productTitle || text,
    productUrl: anchor.href,
    productPricePence: ds.productPricePence
      ? parseInt(ds.productPricePence, 10) || null
      : null,
  };
}

export default function CheckoutGate() {
  const auth = useUserAuth?.() || null;
  const authedEmail = auth?.user?.email || null;
  const [open, setOpen] = useState(false);
  const [pending, setPending] = useState(null);
  const [email, setEmail] = useState("");
  const [touched, setTouched] = useState(false);
  const inputRef = useRef(null);
  const authedEmailRef = useRef(authedEmail);
  useEffect(() => {
    authedEmailRef.current = authedEmail;
  }, [authedEmail]);

  // --------------------------------------------------------------------
  // Global Stripe link interceptor
  // --------------------------------------------------------------------
  useEffect(() => {
    function onClick(e) {
      if (e.defaultPrevented) return;
      if (e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      const anchor = e.target.closest && e.target.closest('a[href*="buy.stripe.com"]');
      if (!anchor) return;
      if (anchor.dataset?.skipCheckoutGate === "true") return;

      const meta = deriveProductMeta(anchor);
      const signedInEmail = authedEmailRef.current || getSignedInEmail();
      const cachedEmail = readCachedEmail();
      const knownEmail = signedInEmail || cachedEmail;

      // Path A — we already know the buyer. Track + prefill + let nav proceed.
      if (knownEmail) {
        trackCheckoutIntent({
          productKey: meta.productKey,
          productTitle: meta.productTitle,
          productUrl: meta.productUrl,
          productPricePence: meta.productPricePence,
          email: knownEmail,
        });
        const next = appendPrefilledEmail(anchor.href, knownEmail);
        if (next && next !== anchor.href) {
          anchor.href = next;
        }
        return;
      }

      // Path B — visitor previously skipped within the quiet window.
      if (isSkipActive()) {
        trackCheckoutIntent({
          productKey: meta.productKey,
          productTitle: meta.productTitle,
          productUrl: meta.productUrl,
          productPricePence: meta.productPricePence,
        });
        return;
      }

      // Path C — intercept and capture.
      e.preventDefault();
      setPending({ meta, target: anchor.target || "_self" });
      setEmail("");
      setTouched(false);
      setOpen(true);
    }
    document.addEventListener("click", onClick, true);
    return () => document.removeEventListener("click", onClick, true);
  }, []);

  useEffect(() => {
    if (open && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 30);
    }
  }, [open]);

  // --------------------------------------------------------------------
  // Submit / skip / close
  // --------------------------------------------------------------------
  const trimmed = email.trim();
  const looksValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed);

  function closeModal() {
    setOpen(false);
    setPending(null);
  }

  function proceed(targetUrl) {
    if (!pending) return;
    if (pending.target === "_blank") {
      window.open(targetUrl, "_blank", "noopener,noreferrer");
    } else {
      window.location.href = targetUrl;
    }
    closeModal();
  }

  function handleSubmit(e) {
    e.preventDefault();
    setTouched(true);
    if (!looksValid || !pending) return;
    const cleaned = trimmed.toLowerCase();
    persistEmail(cleaned);
    trackCheckoutIntent({
      productKey: pending.meta.productKey,
      productTitle: pending.meta.productTitle,
      productUrl: pending.meta.productUrl,
      productPricePence: pending.meta.productPricePence,
      email: cleaned,
    });
    proceed(appendPrefilledEmail(pending.meta.productUrl, cleaned));
  }

  function handleSkip() {
    if (!pending) return;
    markSkip();
    trackCheckoutIntent({
      productKey: pending.meta.productKey,
      productTitle: pending.meta.productTitle,
      productUrl: pending.meta.productUrl,
      productPricePence: pending.meta.productPricePence,
    });
    proceed(pending.meta.productUrl);
  }

  if (!open || !pending) return null;

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-label="Continue to secure checkout"
      data-testid="checkout-gate-modal"
      className="fixed inset-0 z-[1100] flex items-center justify-center bg-black/55 backdrop-blur-sm px-4 py-6"
      onClick={(e) => {
        if (e.target === e.currentTarget) closeModal();
      }}
    >
      <div className="relative w-full max-w-md bg-white rounded-2xl shadow-2xl overflow-hidden">
        <button
          type="button"
          onClick={closeModal}
          aria-label="Close"
          data-testid="checkout-gate-close"
          className="absolute top-3 right-3 w-9 h-9 rounded-full bg-slate-100 hover:bg-slate-200 flex items-center justify-center text-slate-600 transition"
        >
          <X className="w-4 h-4" />
        </button>

        <div className="px-7 pt-8 pb-6">
          <div className="flex items-center gap-2.5 mb-4">
            <div className="w-10 h-10 rounded-xl bg-[#E63B2E]/10 flex items-center justify-center text-[#E63B2E]">
              <Mail className="w-5 h-5" />
            </div>
            <span className="text-[11px] font-bold uppercase tracking-[0.25em] text-slate-500">
              One quick step
            </span>
          </div>

          <h3 className="tdf-heading text-2xl text-tdf-navy leading-tight">
            Send my access link here
          </h3>

          <p className="text-sm text-slate-600 mt-2">
            Enter your email so your receipt and TDF dashboard access are ready
            immediately after checkout.
          </p>

          <p className="text-xs text-slate-500 mt-2 leading-relaxed">
            We use secure link login, so you won&apos;t need a password. Your
            email is used for your purchase, access and important order updates
            only. It is never shared.
          </p>

          <form onSubmit={handleSubmit} className="mt-5">
            <label
              htmlFor="checkout-gate-email"
              className="block text-xs font-bold uppercase tracking-wider text-slate-500 mb-1.5"
            >
              Email address
            </label>
            <input
              ref={inputRef}
              id="checkout-gate-email"
              data-testid="checkout-gate-email-input"
              type="email"
              inputMode="email"
              autoComplete="email"
              spellCheck="false"
              required
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onBlur={() => setTouched(true)}
              placeholder="you@example.com"
              className={`w-full rounded-xl border-2 px-4 py-3 text-base text-tdf-navy placeholder:text-slate-400 focus:outline-none transition ${
                touched && !looksValid
                  ? "border-red-400 focus:border-red-500"
                  : "border-slate-200 focus:border-[#E63B2E]"
              }`}
            />
            {touched && !looksValid && (
              <p
                className="text-xs text-red-600 mt-1.5"
                data-testid="checkout-gate-error"
              >
                Please enter a valid email so we can deliver your purchase.
              </p>
            )}

            <button
              type="submit"
              data-testid="checkout-gate-submit"
              disabled={!looksValid}
              className="mt-4 w-full inline-flex items-center justify-center gap-2 rounded-full bg-[#E63B2E] hover:bg-[#c92f24] disabled:bg-slate-300 disabled:text-white px-6 py-3.5 text-white font-bold shadow-lg transition"
            >
              <Lock className="w-4 h-4" />
              Continue to secure checkout
            </button>
          </form>

          <div className="mt-4 flex items-center justify-center gap-2 text-[11px] text-slate-500">
            <ShieldCheck className="w-3.5 h-3.5 text-emerald-600" />
            <span>Stripe secure · Immediate access</span>
          </div>

          <div className="mt-4 text-center">
            <button
              type="button"
              onClick={handleSkip}
              data-testid="checkout-gate-skip"
              className="text-[11px] text-slate-400 hover:text-slate-600 underline underline-offset-2 transition"
            >
              Continue without email
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
